/* eslint-disable react/jsx-max-depth */
import { useTour } from '@reactour/tour';
import classNames from 'classnames';
import _ from 'lodash/fp';
import React, {
  lazy,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Col,
  Dropdown,
  Overlay,
  OverlayTrigger,
  Row,
  Tooltip,
} from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import Button from '../../components/button/Button';
import MembershipButton from '../../components/button/MembershipButton';
import MeatballDropdown from '../../components/dropdown/MeatballDropdown';
import { DELETE_PROJECT_MODAL } from '../../components/modal/DeleteProjectModal';
import { DEMO_PROJECT_ACTION_MODAL } from '../../components/modal/DemoProjectActionsModal';
import { INITIATE_TOUR_MODAL } from '../../components/modal/InitiateTourModal';
import { MERGE_PROJECT_MODAL } from '../../components/modal/MergeProjectModal';
import { ADD_NOTE_MODAL } from '../../components/modal/addNoteModal/AddNoteModal';
import { INVITE_PROJECT_USERS_MODAL } from '../../components/modal/inviteProjectUsersModal/InviteProjectUsersModal';
import { POINTERRA_ID_MODAL } from '../../components/modal/pointerraIDModal/PointerraIDModal';
import { UPDATE_PRODUCT_MODAL } from '../../components/modal/updateProductModal/UpdateProductModal';
import { UPLOAD_ASSET_MODAL } from '../../components/modal/uploadAssetModal';
import Page from '../../components/page/Page';
import { AMPLITUDE_EVENTS } from '../../constants/analytics';
import { DOWNLOAD_ALL_JOB_STATUS } from '../../constants/asset';
import { FETCH_STATUS } from '../../constants/project';
import useBreakpoint from '../../hooks/useBreakpoint';
import useEventListener from '../../hooks/useEventListener';
import { usePoll } from '../../hooks/usePoll';
import useProjectPermissions from '../../hooks/useProjectPermissions';
import { useRedirect } from '../../hooks/useRedirect';
import useUpdateBookmarkRequest from '../../hooks/useUpdateBookmarkRequest';
import {
  alertError,
  alertSuccess,
  alertWarning,
  toggleStaffActivities,
} from '../../redux/actions';
import { hideModal, showModal } from '../../redux/actions/modal';
import {
  generateDownloadAllUrl,
  getDownloadAllUrl,
  getLatestProject,
  getProjectById,
  resumeDownloadAll,
  setCurrentProject,
} from '../../redux/actions/project';
import { getTourStatus } from '../../redux/actions/tour';
import {
  DOWNLOAD_ALL_STATUS,
  DOWNLOAD_ALL_URL,
  DOWNLOAD_ALL_URL_FAILURE,
} from '../../redux/constants';
import { profileSelectors } from '../../redux/selectors/profile';
import routes from '../../routes/constants';
import text from '../../text';
import { amplitudeTrack } from '../../utilities';
import { downloadFile } from '../../utilities/files';
import { getProjectLatestJob, isDemoProject } from '../../utilities/project';
import { isUserAnonymous } from '../../utilities/user';
import { LayoutContext } from '../LayoutContext';
import { TOURS } from '../dashboard/components/onboarding/tour';
import ActivityFilterControls from './ActivityFilterControls';
import FileManagerFallbackOverlay from './FileManagerFallbackOverlay';
import JobTimeline from './JobTimeline';
import {
  rtkApi,
  useCancelDownloadAllMutation,
  useCreateProjectSampleMutation,
  useEngineQuoteMutation,
  useGetProjectQuery,
  useReinitiateJobMutation,
  useRemoveProjectSampleMutation,
} from '../../redux/rtk';
import useUser from '../../mp/hooks/useUser';
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from '../../mp/components/Dialog';
import Loading from '../../components/loading/Loading';
import { TEST_DATA } from '../../components/FileManager/PdfGenerator';
import FileManagerProvider from '../../components/FileManager/Provider';
import QuotePdfPreview from '../../components/FileManager/QuotePdfPreview';
import IconButton from '../../components/button/IconButton';
import Icon from '../../components/icon/Icon';
import useScrollToElement from '@/hooks/useScrollToElement';
import DownloadAllButton from '../../components/FileManager/DownloadAllButton';

const { FILE_MANAGER } = AMPLITUDE_EVENTS;

// TODO: Refactor to TS when most of the components are converted to TS
const FileManager = () => {
  const history = useHistory();
  const redirect = useRedirect();
  const { projectId } = useParams();
  const dispatch = useDispatch();
  const { currentProject, getCurrentProjectStatus, downloadAll, tourSelected } =
    useSelector((state) => ({
      currentProject: state.project.currentProject,
      getCurrentProjectStatus: state.project.getCurrentProjectStatus,
      currentUser: state.profileReducer.userProfile,
      isStaffActivityListVisible: state.activity.isStaffActivityListVisible,
      downloadAll: state.project.downloadAll,
      tourSelected: state.tourReducer.tourSelected,
    }));

  const { data: projectData } = useGetProjectQuery({
    projectId,
    params: { includeActivities: true },
  });
  const activities = projectData?.activities || [];
  const isAdmin = useSelector(profileSelectors.getIsAdmin);
  const { setStartNewProjectHighlight } = useContext(LayoutContext);
  const downloadAllState = downloadAll[projectId];
  const [zipProgress, setZipProgress] = useState(0);
  const [isNavBarSticky, setNavBarIsSticky] = useState(null);
  const fileManagerContainerRef = useRef(null);

  const getProjectState = useGetProjectQuery({ projectId });

  const currentUser = useUser();
  const isUserLoggedIn = useSelector(
    (state) => state.profileReducer.isUserLoggedIn
  );
  const isOnboardingRequired = useMemo(() => {
    const profile = currentUser.profile;
    if (!isUserLoggedIn || !profile || isUserAnonymous(profile)) {
      return false;
    }

    return !(profile.full_name && profile.profession);
  }, [currentUser.profile, isUserLoggedIn]);

  const [engineQuote, engineQuoteState] = useEngineQuoteMutation();
  const [reinitiateJob, reinitiateJobState] = useReinitiateJobMutation();
  const [cancelDownloadAllMutation] = useCancelDownloadAllMutation();
  const [createProjectSample, createProjectSampleState] =
    useCreateProjectSampleMutation();
  const [removeProjectSample, removeProjectSampleState] =
    useRemoveProjectSampleMutation();

  // side effect that redirects to request access
  // page if unauthorized to view this project.
  useEffect(() => {
    const err = getProjectState.error;
    if (err && err.status === 403) {
      dispatch(alertWarning('Requesting access...'));
      const page = history.location.pathname;
      redirect.push(routes.requestAccess.project.url(projectId), {
        queryParams: { page },
      });
    }
  }, [
    dispatch,
    getProjectState.error,
    history.location.pathname,
    projectId,
    redirect,
  ]);

  const hasCurrentProject = useMemo(
    () => !!currentProject?.id,
    [currentProject]
  );

  // fetch the project by projectId if present, latest otherwise
  useEffect(() => {
    if (projectId) {
      dispatch(
        getProjectById(projectId, {
          includeJobs: true,
          includePointCloud: true,
          includeQuote: true,
        })
      );
    } else {
      dispatch(
        getLatestProject({
          isAdmin: false,
          includeDemo: false,
          includeJobs: true,
          includePointCloud: true,
        })
      );
    }

    // if current project is a demo, reset the current project state so returning to the file manager
    return () => {
      if (currentProject?.id && isDemoProject(currentProject))
        dispatch(setCurrentProject(null));
    };
  }, [projectId]);

  // once fetched if projectId not present, redirect to that project's URL
  useEffect(() => {
    if (
      !projectId &&
      getCurrentProjectStatus === FETCH_STATUS.DONE &&
      currentProject?.id
    ) {
      redirect.push(`${routes.fileManager.root}/${currentProject.id}`);
    }
  }, [getCurrentProjectStatus, currentProject]);

  const isFileManagerFallback = useMemo(
    () => getCurrentProjectStatus === FETCH_STATUS.DONE && !hasCurrentProject,
    [getCurrentProjectStatus, hasCurrentProject]
  );

  useEffect(() => {
    setStartNewProjectHighlight(isFileManagerFallback);
    return () => {
      setStartNewProjectHighlight(false);
    };
  }, [isFileManagerFallback, setStartNewProjectHighlight]);

  const latestJob = useMemo(() => {
    if (!currentProject) return null;
    const job = getProjectLatestJob(currentProject);
    return {
      ...job,
      project: currentProject,
      ..._.pick(
        ['user_project', 'jobs', 'point_clouds', 'updated_at'],
        currentProject
      ),
    };
  }, [currentProject]);
  const hasLatestJob = useMemo(() => !_.isEmpty(latestJob), [latestJob]);
  const projectPermissions = useProjectPermissions(currentProject);

  const zipProgressPct = useMemo(
    () => Math.floor(100 * zipProgress),
    [zipProgress]
  );

  const handle2DMapClick = (event) => {
    event.preventDefault();
    if (!hasLatestJob) {
      console.warn('tried 2D map click without latest job');
      return;
    }
    const _2dMapRoute = latestJob.map2d_id
      ? routes.mp.map2d(latestJob.map2d_id)
      : routes.order.job(latestJob.id);
    redirect.push(isAdmin ? `${_2dMapRoute}?admin=true` : _2dMapRoute);
  };

  // Staff manage Samples, so only everyone else gets the read-only nudge.
  const isSampleLockedForUser = useMemo(
    () => isDemoProject(currentProject) && !currentUser.isStaff,
    [currentProject, currentUser.isStaff]
  );

  const isSampleActionPending =
    createProjectSampleState.isLoading || removeProjectSampleState.isLoading;

  const handleMakeSample = useCallback(async () => {
    try {
      const { project: sampleProject } = await createProjectSample({
        projectId,
      }).unwrap();
      dispatch(
        alertSuccess(text('sampleProjectCreated'), { icon: 'circled-tick' })
      );
      redirect.push(routes.fileManager.viewProject(sampleProject.id));
    } catch (error) {
      // Stay on the original project so a half-made Sample is never shown.
      dispatch(
        alertError(error?.data?.message ?? text('sampleProjectCreateFailed'))
      );
    }
  }, [createProjectSample, dispatch, projectId, redirect]);

  const handleRemoveSample = useCallback(async () => {
    try {
      await removeProjectSample({ projectId }).unwrap();
      dispatch(
        alertSuccess(text('sampleProjectRemoved'), { icon: 'circled-tick' })
      );
      // currentProject comes from the thunk, not RTK, so tag invalidation alone
      // would leave the menu offering "Remove as Sample".
      dispatch(
        getProjectById(projectId, {
          includeJobs: true,
          includePointCloud: true,
          includeQuote: true,
        })
      );
    } catch (error) {
      dispatch(
        alertError(error?.data?.message ?? text('sampleProjectRemoveFailed'))
      );
    }
  }, [dispatch, projectId, removeProjectSample]);

  const handle3DViewerClick = (event) => {
    event.preventDefault();
    if (!projectId) {
      console.warn('tried 3D viewer click without projectId');
      return;
    }
    redirect.push(
      isAdmin
        ? `${routes.view3DExternal.root}/${projectId}?admin=true`
        : `${routes.view3DExternal.root}/${projectId}`
    );
  };

  const { poll: pollDownload } = usePoll({
    callback: async (stopPolling) => {
      if (!projectId || !downloadAllState?.queueJobId) {
        return;
      }
      const data = await dispatch(
        getDownloadAllUrl(projectId, downloadAllState.queueJobId)
      );
      if (!data) {
        stopPolling();
        return;
      }
      const { status, url, zipProgress: newZipProgress } = data;
      switch (status) {
        case DOWNLOAD_ALL_JOB_STATUS.AVAILABLE:
          downloadFile(url);
          stopPolling();
          break;
        case DOWNLOAD_ALL_JOB_STATUS.PROCESSING:
          setZipProgress(newZipProgress);
          break;
        case DOWNLOAD_ALL_JOB_STATUS.FAILED:
          console.error('failed to generate download URL');
          stopPolling();
          break;
        default:
          console.error(`unknown status: ${status}`);
          stopPolling();
          break;
      }
    },
    dependencies: [projectId, downloadAllState?.queueJobId],
    startEnabled: false,
    delay: 5000,
  });

  const handleDownloadAll = async ({ downloadIfAvailable } = {}) => {
    const activitiesWithAssets = activities.filter(
      (activity) => activity.created_assets?.length > 0
    );
    const allActivitiesSuperseded =
      activitiesWithAssets.length > 0 &&
      activitiesWithAssets.every(
        (activity) =>
          activity.superseded_assets?.length === activity.created_assets?.length
      );

    if (allActivitiesSuperseded) {
      dispatch(alertWarning(text('downloadAllUnavailable')));
      return;
    }

    const data = await dispatch(generateDownloadAllUrl(projectId));
    if (!data) {
      return;
    }
    const { status, url } = data;

    switch (status) {
      case DOWNLOAD_ALL_JOB_STATUS.AVAILABLE:
        if (downloadIfAvailable) {
          downloadFile(url);
        }
        break;
      case DOWNLOAD_ALL_JOB_STATUS.PROCESSING:
        pollDownload(true);
        break;
      case DOWNLOAD_ALL_JOB_STATUS.UNAVAILABLE:
      case DOWNLOAD_ALL_JOB_STATUS.FAILED:
        break;
      default:
        console.error('unknown status', status);
        break;
    }
  };

  const handleDownloadAllClick = async () => {
    amplitudeTrack(FILE_MANAGER.EVENT, {
      action: FILE_MANAGER.DOWNLOAD_ALL_BUTTON,
    });

    await handleDownloadAll({ downloadIfAvailable: true });
  };

  const downloadAllResumed = useRef(false);
  useEffect(() => {
    if (projectId) {
      dispatch(resumeDownloadAll(projectId));
    }
  }, [projectId]);
  useEffect(() => {
    if (downloadAllResumed.current) {
      return;
    }
    if (
      projectId &&
      downloadAllState?.isLoading &&
      downloadAllState?.queueJobId
    ) {
      pollDownload(true);
      downloadAllResumed.current = true;
    }
  }, [projectId, downloadAllState?.queueJobId]);

  /* ---------- TOURS ---------- */
  const { steps, setSteps, setCurrentStep } = useTour();
  const hasRequestedTour = useRef(false);

  useEffect(() => {
    if (isOnboardingRequired) return;
    if (steps.length && tourSelected) {
      dispatch(showModal(INITIATE_TOUR_MODAL));
    }
  }, [steps, tourSelected, dispatch, isOnboardingRequired]);

  useEffect(() => {
    if (hasRequestedTour.current || isOnboardingRequired) {
      return;
    }
    // need to only start the tour if a valid project
    if (getCurrentProjectStatus === FETCH_STATUS.DONE && hasCurrentProject) {
      dispatch(
        getTourStatus(TOURS.TOUR_FILE_MANAGER, setSteps, setCurrentStep)
      );
      hasRequestedTour.current = true;
    }
    return () => {
      setSteps([]); // clean up tour steps on unmount
    };
  }, [
    dispatch,
    getCurrentProjectStatus,
    hasCurrentProject,
    isOnboardingRequired,
    setSteps,
    setCurrentStep,
  ]);

  useEventListener(
    'scroll',
    (e) => {
      const { scrollTop } = e.target;
      if (!fileManagerContainerRef.current?.contains(e.target)) {
        return;
      }

      // only reset to false when scrolled back to top
      // to avoid changing header in middle of interaction when
      // the scrollTop < 250, might confuse the user the header is gone
      if (scrollTop === 0) {
        setNavBarIsSticky(false);
      }

      if (scrollTop > 0) {
        setNavBarIsSticky(true);
      }
    },
    {},
    true
  );

  useEffect(() => {
    if (reinitiateJobState.error) {
      dispatch(alertError(reinitiateJobState.error.data?.message));
    }
  }, [reinitiateJobState.error, dispatch]);

  const isScreenTabletAndUp = useBreakpoint(['md', 'lg', 'xl']);

  const { isUpdatingBookmarkRequest } = useUpdateBookmarkRequest();

  // This seems like isFileManagerFallback but this is necessary to avoid
  // the header from showing when the project is not loaded
  const isHeaderVisible = useMemo(
    () => getCurrentProjectStatus === FETCH_STATUS.DONE && hasCurrentProject,
    [getCurrentProjectStatus, hasCurrentProject]
  );

  const handleCancelDownload = async (e) => {
    e.stopPropagation();
    if (downloadAllState?.queueJobId) {
      try {
        // legacy
        dispatch({
          type: DOWNLOAD_ALL_URL,
          payload: { projectId },
        });

        const response = await cancelDownloadAllMutation({
          projectId,
          queueJobId: downloadAllState.queueJobId,
        }).unwrap();

        // legacy
        if (response && response.cancelled) {
          dispatch({
            type: DOWNLOAD_ALL_URL_FAILURE, // Reset the download status
            payload: { projectId },
          });
        }
      } catch (error) {
        // legacy
        dispatch({
          type: DOWNLOAD_ALL_URL_FAILURE,
          payload: { projectId },
        });
        dispatch(
          alertError(
            error.response?.data?.message
              ? error.response.data.message
              : text('downloadAllCancelFailed')
          )
        );
      }
    }
  };

  const clearAssetIdAfterScrollToElement = useCallback(() => {
    try {
      const updatedParams = new URLSearchParams(history.location.search);
      updatedParams.delete('asset');
      const newSearch = updatedParams.toString();
      history.replace({
        ...history.location,
        search: newSearch ? `?${newSearch}` : '',
      });
    } catch (e) {
      console.log('Error deleting asset query parameter: ', e);
    }
  }, [history]);

  const assetId = useMemo(() => {
    const search = history.location?.search;
    if (!search) return null;
    const params = new URLSearchParams(search);
    return params.get('asset');
  }, [history.location?.search]);

  useScrollToElement(assetId, fileManagerContainerRef, (el) => {
    el.style.transition = 'background-color 400ms ease';
    el.style.backgroundColor = 'rgba(255, 235, 59, 0.35)';
    el.style.fontWeight = '700';
    clearAssetIdAfterScrollToElement();
  });

  return (
    <FileManagerProvider
      value={{ scrollContainerRef: fileManagerContainerRef }}
    >
      <Page
        className='FileManager'
        title={text('fileManager')}
        noPadding
        showTitlebar={false}
        style={{ overflowY: 'hidden' }}
      >
        <div className='scroll-container' ref={fileManagerContainerRef}>
          {isUpdatingBookmarkRequest ? (
            <div className='progress-bar top-appbar-loader'>
              <div className='progress-bar-value' />
            </div>
          ) : null}
          <div
            className={classNames({
              'is-sticky elevation': isScreenTabletAndUp && isNavBarSticky,
              'sticky-top': isScreenTabletAndUp,
            })}
          >
            {isHeaderVisible ? (
              <div
                className='topnav d-flex flex-column'
                style={{ width: '100%' }}
              >
                <ActivityFilterControls
                  id='ActivityFilterControls-secondary'
                  projectId={projectId}
                  currentProject={currentProject}
                />
                <div className='Controls'>
                  <Col className='left'>
                    <Button
                      id='inviteProjectUsersButton'
                      variant='outline-primary'
                      icon='user'
                      tooltipProps={{ text: text('inviteViewProjectUsers') }}
                      onClick={() => {
                        amplitudeTrack(FILE_MANAGER.EVENT, {
                          action: FILE_MANAGER.SHARE_BUTTON,
                        });
                        return dispatch(
                          !isSampleLockedForUser
                            ? showModal(INVITE_PROJECT_USERS_MODAL, {
                                onHide: () => {
                                  dispatch(
                                    hideModal(INVITE_PROJECT_USERS_MODAL)
                                  );
                                  dispatch(
                                    rtkApi.util.invalidateTags(['Project'])
                                  );
                                },
                                project: currentProject,
                                job: getProjectLatestJob(currentProject),
                              })
                            : showModal(DEMO_PROJECT_ACTION_MODAL)
                        );
                      }}
                    >
                      {text('share')}
                    </Button>
                    <Button
                      id='addNoteButton'
                      icon='speech'
                      variant='outline-primary'
                      tooltipProps={{ text: text('addNote') }}
                      onClick={() => {
                        amplitudeTrack(FILE_MANAGER.EVENT, {
                          action: FILE_MANAGER.NOTE_BUTTON,
                        });
                        return dispatch(
                          !isSampleLockedForUser
                            ? showModal(ADD_NOTE_MODAL, {
                                onHide: () =>
                                  dispatch(hideModal(ADD_NOTE_MODAL)),
                                job: latestJob,
                              })
                            : showModal(DEMO_PROJECT_ACTION_MODAL)
                        );
                      }}
                    >
                      {text('note')}
                    </Button>
                    <Button
                      id='uploadFileButton'
                      variant='outline-primary'
                      tooltipProps={{ text: text('uploadFiles') }}
                      icon='clip'
                      onClick={() => {
                        amplitudeTrack(FILE_MANAGER.EVENT, {
                          action: FILE_MANAGER.UPLOAD_BUTTON,
                        });
                        return dispatch(
                          !isSampleLockedForUser
                            ? showModal(UPLOAD_ASSET_MODAL, {
                                projectId,
                              })
                            : showModal(DEMO_PROJECT_ACTION_MODAL)
                        );
                      }}
                    >
                      {text('upload')}
                    </Button>
                    <DownloadAllButton projectId={projectId} />
                    <MeatballDropdown
                      tooltipProps={{ text: text('more'), placement: 'top' }}
                      btnClassName='btn btn-outline-primary'
                    >
                      <Dropdown.Item
                        disabled={!projectPermissions.is2dMapEnabled}
                        onClick={handle2DMapClick}
                      >
                        {text('map2D')}
                      </Dropdown.Item>
                      <Dropdown.Item
                        onClick={handle3DViewerClick}
                        disabled={!projectPermissions.is3dViewerEnabled}
                      >
                        {text('viewer3D')}
                      </Dropdown.Item>
                      <Dropdown.Item
                        id='downloadItem'
                        onClick={handleDownloadAllClick}
                      >
                        {text('downloadAll')}
                      </Dropdown.Item>
                      {currentUser.isStaff ? (
                        <Dropdown.Item
                          id='initate'
                          onClick={async () => {
                            if (!latestJob) return;
                            await engineQuote({ jobId: latestJob.id }).unwrap();
                            await reinitiateJob({
                              jobId: latestJob.id,
                            }).unwrap();
                          }}
                        >
                          Manually initiate latest job (staff-only)
                        </Dropdown.Item>
                      ) : null}
                      {projectPermissions.isAdministrationEnabled ? (
                        <>
                          <Dropdown.Divider />
                          <Dropdown.Item
                            disabled={!projectId}
                            onClick={() =>
                              dispatch(
                                showModal(POINTERRA_ID_MODAL, {
                                  onHide: () => {
                                    dispatch(hideModal(POINTERRA_ID_MODAL));
                                    dispatch(
                                      rtkApi.util.invalidateTags(['Project'])
                                    );
                                    // The 3D affordances read point_clouds off
                                    // currentProject, which the thunk owns, so
                                    // the tag alone leaves them disabled until
                                    // a reload.
                                    dispatch(
                                      getProjectById(projectId, {
                                        includeJobs: true,
                                        includePointCloud: true,
                                        includeQuote: true,
                                      })
                                    );
                                  },
                                  job: latestJob,
                                })
                              )
                            }
                          >
                            {text('pointerraHash')}
                          </Dropdown.Item>
                          <Dropdown.Item
                            id='updateProductButton'
                            variant='outline-primary'
                            icon='circle'
                            tooltipProps={{ text: text('updateProductStatus') }}
                            disabled={!projectId}
                            onClick={() =>
                              dispatch(
                                showModal(UPDATE_PRODUCT_MODAL, {
                                  onHide: () =>
                                    dispatch(hideModal(UPDATE_PRODUCT_MODAL)),
                                  job: latestJob,
                                })
                              )
                            }
                          >
                            {text('statusUpdate')}
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() => dispatch(toggleStaffActivities())}
                          >
                            {text('showLarkiLogs')}
                          </Dropdown.Item>
                          <Dropdown.Item
                            id='sampleProjectItem'
                            disabled={!projectId || isSampleActionPending}
                            onClick={
                              isDemoProject(currentProject)
                                ? handleRemoveSample
                                : handleMakeSample
                            }
                          >
                            {isDemoProject(currentProject)
                              ? text('removeAsSample')
                              : text('makeASample')}
                          </Dropdown.Item>
                          <Dropdown.Divider />
                        </>
                      ) : null}
                      {!isSampleLockedForUser && (
                        <>
                          {/* Merging moves the demo job out or a customer's
                              jobs in, and either way one of the two projects
                              ends up listed for every user. */}
                          {!isDemoProject(currentProject) && (
                            <>
                              <Dropdown.Item
                                onClick={() =>
                                  dispatch(
                                    showModal(MERGE_PROJECT_MODAL, {
                                      currentJob: currentProject,
                                      onSuccess: (targetProject) =>
                                        redirect.push(
                                          routes.fileManager.viewProject(
                                            targetProject?.id
                                          )
                                        ),
                                    })
                                  )
                                }
                              >
                                {text('mergeProjects')}
                              </Dropdown.Item>
                              <Dropdown.Divider />
                            </>
                          )}
                          <Dropdown.Item
                            className='red'
                            disabled={!projectPermissions.isDeleteEnabled}
                            onClick={() =>
                              dispatch(
                                showModal(DELETE_PROJECT_MODAL, {
                                  projectId,
                                  name: currentProject.name,
                                  onSuccess: () => {
                                    dispatch(setCurrentProject(null));
                                    redirect.push(routes.dashboard);
                                  },
                                })
                              )
                            }
                          >
                            {text('deleteProject')}
                          </Dropdown.Item>
                        </>
                      )}
                    </MeatballDropdown>
                  </Col>

                  <Col className='right'>
                    <Row>
                      <MembershipButton variant='outline-primary' />
                      {/* On a Sample this dropped the projectId and silently
                          started an unrelated new project. */}
                      {!isDemoProject(currentProject) && (
                        <Button
                          className='add-order-button'
                          variant='primary'
                          onClick={(event) => {
                            event.preventDefault();
                            history.push({
                              pathname: routes.mp.root,
                              search: `?projectId=${currentProject?.projectId}`,
                            });
                          }}
                          icon='plus'
                        >
                          {text('addOrderToProject')}
                        </Button>
                      )}
                    </Row>
                  </Col>
                </div>
                <ActivityFilterControls
                  id='ActivityFilterControls-tour'
                  projectId={projectId}
                  currentProject={currentProject}
                />
              </div>
            ) : null}
          </div>

          <FileManagerFallbackOverlay show={isFileManagerFallback} />
          {hasCurrentProject && isHeaderVisible ? <JobTimeline /> : null}
        </div>
      </Page>
    </FileManagerProvider>
  );
};

export default FileManager;
